import React from 'react';
import PropTypes from 'prop-types';
import './TableAnnotationPanel.scss';

/**
 * TableAnnotationPanel component for displaying table annotations
 * Shows in a modal overlay covering most of the PDF viewer
 * Used for displaying structured data that doesn't fit in the side panel
 * 
 * @param {Object} annotation - The selected annotation object with table data
 * @param {Function} onClose - Callback when panel is closed
 */
const TableAnnotationPanel = ({ annotation, onClose }) => {
  if (!annotation || !annotation.tableData) return null;

  const { title, description, tableData, modalWidth } = annotation;
  const { headers = [], rows = [], footerText, headerText } = tableData;

  const modalStyle = modalWidth ? { width: modalWidth } : {};

  return (
    <div className="table-annotation-panel">
      <div className="table-annotation-panel__overlay" onClick={onClose} />
      
      <div className="table-annotation-panel__modal" style={modalStyle}>
        <div className="table-annotation-panel__header">
          <div className="table-annotation-panel__title-section">
            <h3 className="table-annotation-panel__title">{title}</h3>
            {description && (
              <p className="table-annotation-panel__description">{description}</p>
            )}
          </div>
          <button
            className="table-annotation-panel__close-btn"
            onClick={onClose}
            aria-label="Close table panel"
          >
            ✕
          </button>
        </div>

        <div className="table-annotation-panel__content">
          {headerText && (
            <div className="table-annotation-panel__header-text">
              {headerText}
            </div>
          )}
          <div className="table-annotation-panel__table-wrapper">
            <table className="table-annotation-panel__table">
              <thead>
                <tr>
                  {headers.map((header, idx) => (
                    <th key={idx}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIdx) => (
                  <tr key={rowIdx}>
                    {headers.map((header, colIdx) => (
                      <td key={colIdx}>{row[header]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {footerText && (
            <div className="table-annotation-panel__footer">
              {footerText}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

TableAnnotationPanel.propTypes = {
  annotation: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    tableData: PropTypes.shape({
      headers: PropTypes.arrayOf(PropTypes.string).isRequired,
      rows: PropTypes.arrayOf(PropTypes.object).isRequired,
      headerText: PropTypes.string,
      footerText: PropTypes.string,
    }).isRequired,
  }),
  onClose: PropTypes.func.isRequired,
};

export default TableAnnotationPanel;
